import { Language, Menu, Service } from '@yleisradio/areena-types';
import { AreenaMenu } from 'components/AreenaMenu';
import BrowserSupportAlert from 'components/BrowserSupportAlert';
import { Container } from 'components/Container';
import { ErrorBoundary } from 'components/ErrorBoundary';
import GlobalHeader from 'components/GlobalHeader';
import { NotificationToaster } from 'components/Notifications';
import { Notification } from 'components/Notifications/Notification';
import { SkipLink } from 'components/SkipLink';
import { YleAnalytics } from 'components/YleAnalytics';
import { YleTunnus } from 'components/YleTunnus';
import { AnalyticsAvailabilityProvider } from 'contexts/AnalyticsAvailabilityContext';
import { CookieConsentContextProvider } from 'contexts/CookieConsentContext';
import { TunnusContextProvider } from 'contexts/TunnusContext';
import { useTranslation } from 'hooks/useTranslation';
import { IncomingMessage } from 'http';
import type {
  AppContext,
  AppInitialProps,
  AppProps as NextAppProps,
} from 'next/app';
import NextApp from 'next/app';
import { NextApiRequestCookies } from 'next/dist/server/api-utils';
import Script from 'next/script';
import React, { useEffect } from 'react';
import * as ApmRum from 'services/apm-rum';
import {
  fetchBrowseMenuContentData,
  getAppData,
} from 'services/areena-api/fetchers';
import { getGlobalHeader } from 'services/global-header';
import { chatScriptUrl } from 'services/properties/frontend';
import 'styles/global.scss';
import { SWRConfig } from 'swr';
import { globalSWRConfig } from 'utils/fetch';
import { isAuthenticated } from 'utils/login';
import { getCspDirectives, getHashForHeaderScript } from 'utils/security/csp';
import { Header } from 'components/Navigation/Header';
import { AreenaFooter } from 'components/AreenaFooter';
import { AreenaServiceProvider } from 'contexts/AreenaServiceContext';
import { BrowseMenuContent } from '@yleisradio/areena-types/domain/v2/browseMenuContent';

ApmRum.init();

const cleanUpUnusedLocalStorageItem = (): void => {
  window.localStorage.removeItem('isPodcastsTooltipDismissed');
};

type AppProps = {
  menus: Menu[];
  userIsAuthenticated: boolean;
  yleGlobalHeaderHtml: string;
  featureNavigation2024Enabled: boolean;
  browseMenuContent: BrowseMenuContent | null;
};

type AppRequest = IncomingMessage & {
  cookies: NextApiRequestCookies;
};

function App({
  pageProps,
  Component,
  yleGlobalHeaderHtml,
  userIsAuthenticated,
  menus,
  featureNavigation2024Enabled,
  browseMenuContent,
}: AppProps & NextAppProps): JSX.Element {
  useEffect(cleanUpUnusedLocalStorageItem, []);

  const t = useTranslation();

  const errorFallback = (
    <Container>
      <Notification
        helpText={t('notificationHelpTextGeneric')}
        notification={{ uiMessage: t('genericError') }}
        notificationStyle="error"
      />
    </Container>
  );

  return (
    <ErrorBoundary fallback={errorFallback}>
      <div key={'browserSupport'}>
        <BrowserSupportAlert />
      </div>
      <SWRConfig value={globalSWRConfig}>
        <TunnusContextProvider isAuthenticated={userIsAuthenticated}>
          <AnalyticsAvailabilityProvider>
            <CookieConsentContextProvider>
              <AreenaServiceProvider>
                <SkipLink />
                <YleAnalytics />
                {!featureNavigation2024Enabled && (
                  <GlobalHeader globalHeaderHtml={yleGlobalHeaderHtml} />
                )}
                <YleTunnus />
                {!featureNavigation2024Enabled && <AreenaMenu menus={menus} />}
                {featureNavigation2024Enabled && (
                  <Header browseMenuContent={browseMenuContent} />
                )}
                <ErrorBoundary fallback={errorFallback}>
                  <Component {...pageProps} />
                </ErrorBoundary>
                <NotificationToaster />
                {featureNavigation2024Enabled && <AreenaFooter />}
              </AreenaServiceProvider>
            </CookieConsentContextProvider>
          </AnalyticsAvailabilityProvider>
        </TunnusContextProvider>
      </SWRConfig>
      <Script src={chatScriptUrl} strategy="lazyOnload" />
    </ErrorBoundary>
  );
}

App.getInitialProps = async (
  appContext: AppContext
): Promise<AppInitialProps & AppProps> => {
  const { cookies } = appContext.ctx.req as AppRequest;
  const { locale } = appContext.router;

  const language: Language = locale === 'sv' ? 'sv' : 'fi';
  const userIsAuthenticated = isAuthenticated(cookies);

  const path = appContext.ctx.asPath || '';
  const service: Service =
    path.startsWith('/poddar') || path.startsWith('/podcastit')
      ? 'radio'
      : 'tv';

  const featureNavigation2024Enabled =
    process.env.FEATURE_NAVIGATION_2024 === 'true';

  const [appProps, appDataResponse, yleGlobalHeaderHtml] = await Promise.all([
    NextApp.getInitialProps(appContext),
    getAppData(language, service),
    getGlobalHeader(language),
  ]);

  const browseMenuContent = featureNavigation2024Enabled
    ? await fetchBrowseMenuContentData(service, language)
    : null;

  const headerScriptHash = await getHashForHeaderScript(yleGlobalHeaderHtml);
  appContext.ctx.res?.setHeader(
    'Content-Security-Policy',
    getCspDirectives(headerScriptHash)
  );

  appContext.ctx.res?.setHeader(
    'Referrer-Policy',
    'strict-origin-when-cross-origin'
  );

  appContext.ctx.res?.setHeader('Strict-Transport-Security', 'max-age=7776000');
  appContext.ctx.res?.setHeader('X-Content-Type-Options', 'nosniff');

  const menus = appDataResponse?.menus || [];

  return {
    ...appProps,
    menus,
    userIsAuthenticated,
    yleGlobalHeaderHtml,
    featureNavigation2024Enabled,
    browseMenuContent,
  };
};

export default App;
